import type { ReactElement, RefObject } from 'react'
import { FileSpreadsheet, RefreshCw, X } from 'lucide-react'

interface ModalHeaderProps {
  onClose: () => void
}

export function ModalHeader({ onClose }: ModalHeaderProps): ReactElement {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Import from CSV</h3>
      <button
        onClick={onClose}
        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

export function ApiErrorBanner({ apiError }: { apiError: string }): ReactElement {
  return (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
      <p className="text-sm text-red-700 dark:text-red-400">{apiError}</p>
    </div>
  )
}

interface SelectStepProps {
  fileInputRef: RefObject<HTMLInputElement>
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  onClose: () => void
}

export function SelectStep({
  fileInputRef,
  handleFileChange,
  onClose,
}: SelectStepProps): ReactElement {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Upload a CSV file with columns:{' '}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">hostname</code>{' '}
        (required),
        <code className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          device_type
        </code>
        ,
        <code className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          ip_addresses
        </code>
        ,
        <code className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          domain
        </code>
        ,
        <code className="ml-1 rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
          operating_system
        </code>
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={(...args: Parameters<typeof handleFileChange>) => {
          void handleFileChange(...args)
        }}
        className="hidden"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-8 transition-colors hover:border-lime-400 dark:border-slate-600 dark:hover:border-[#4e5e82]"
      >
        <FileSpreadsheet className="h-8 w-8 text-slate-400" />
        <span className="text-slate-600 dark:text-slate-400">Click to select CSV file</span>
      </button>
      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export function ImportingStep({ count }: { count: number }): ReactElement {
  return (
    <div className="space-y-4 py-8">
      <div className="flex flex-col items-center justify-center gap-4">
        <RefreshCw className="h-12 w-12 animate-spin text-lime-600" />
        <p className="text-lg font-medium text-slate-700 dark:text-slate-300">
          Importing {count} endpoints...
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Please wait while your data is being imported.
        </p>
      </div>
    </div>
  )
}
